import { COL_NAME_CAMERA, COL_NAME_CAUTION, COL_NAME_DAYTIME, COL_NAME_MANAGEMENT_ID, COL_NAME_NIGHTTIME, COL_NAME_ROUTE_DATA, COL_NAME_ROUTE_NAME, COL_NAME_TRAFFIC_DIRECTION, COL_NAME_TRAFFIC_LINKED_SENSORS, COL_NAME_WARNING, MSG_DELETE_SUCCESS, MSG_ERROR, TXT_CREATE_NEW, TXT_SAVE, TXT_START_DAYTIME, TXT_START_NIGHTTIME, TXT_CAUTION_COLOR, TXT_WARNING_COLOR, COL_NAME_SCENARIO_NO, MSG_CONFIRM_DELETE } from './constants';
import './TrafficMonitoring.css';
import React, { useEffect, useMemo, useState } from 'react';
import TimePicker from '../../components/TimePicker';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { useDialog } from 'context/DialogContext';
import { uploadData } from 'aws-amplify/storage';
import { DIALOG_MESSAGE_UPDATED_GENERAL_SETTING_SUCCESSFULLY, TEXT_SAVE_FAILURE } from 'constants/message';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiDeleteTrafficMonitoring, apiGetListTrafficMonitoring, apiUpdateTrafficMonitoring } from 'apis/TrafficMonitoringApi';
import { useSensors } from 'context/SensorsContext';
import Pagination from './components/Pagination';
import { TRAFFIC_MONITORING_LIST } from 'pages/Location/constants';
import { AccountType } from 'models';
import { useAuth } from 'context/AuthContext';
import LocationListHeader from 'components/LocationListHeader';
import { standardizeString } from 'utils/function';

function TrafficMonitoring() {
    const { register, handleSubmit, control, reset } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [trafficMonitoringSettings, setTrafficMonitoringSettings] = useState([]);
    const [pagination, setPagination] = useState({ nextToken: null, curPage: 1, perPage: 150 });
    const { generalSetting, setGeneralSetting } = useGeneralSetting();
    const { trafficMonitoring } = generalSetting || {};
    const { error: errorDialog, success, warningConfirm } = useDialog();
    const [keyword, setKeyword] = useState('');
    const navigate = useNavigate();
    const { allSensors } = useSensors();
    const { currentUser } = useAuth();
    const isAdmin = useMemo(() => [AccountType.ADMIN, AccountType.MANAGER].includes(currentUser?.accountType), [currentUser]);

    const onSearch = (originalKeyword, keywords) => {
        setKeyword(originalKeyword);
    }

    const SENSOR_MAPPING = useMemo(() => {
        if (!allSensors || allSensors.length === 0)
            return []
        return allSensors.reduce((acc, sensor) => {
            acc[sensor.id] = sensor.name;
            return acc;
        }, {});
    }, [allSensors]);

    useEffect(() => {
        if (!generalSetting) {
            setIsLoading(true);
        }
    }, [generalSetting]);

    useEffect(() => {
        reset({
            daytimeStart: trafficMonitoring?.daytimeStart,
            nighttimeStart: trafficMonitoring?.nighttimeStart,
            cautionColor: trafficMonitoring?.cautionColor,
            warningColor: trafficMonitoring?.warningColor,
        });
    }, [trafficMonitoring]);

    const fetch = async () => {
        try {
            setIsLoading(true);
            const { items, nextToken } = await apiGetListTrafficMonitoring(pagination.perPage, pagination.nextToken, false, 'ASC');
            setTrafficMonitoringSettings(items);
            setPagination(prev => ({ ...prev, nextToken }));
        } catch (error) {
            errorDialog(MSG_ERROR);
            console.error("Failed to fetch traffic monitoring data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetch();
    }, []);

    const fetchData = async () => {
        try {
            if (pagination.curPage === 1 ||
                !pagination.nextToken ||
                Math.floor(trafficMonitoringSettings.length / pagination.perPage) >= pagination.curPage) return;
            setIsLoading(true);
            const { items, nextToken } = await apiGetListTrafficMonitoring(pagination.perPage, pagination.nextToken, false, 'ASC');
            setTrafficMonitoringSettings(prev => [...prev, ...items]);
            setPagination(prev => ({ ...prev, nextToken }));
        } catch (error) {
            errorDialog(MSG_ERROR);
            console.error("Failed to fetch traffic monitoring data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination.curPage]);

    const onApplySettings = handleSubmit(async (data) => {
        try {
            if (data.daytimeStart === data.nighttimeStart) {
                warningConfirm("昼間開始時間と夜間開始時間は同じにできません。");
                return;
            }

            setIsLoading(true);
            const newData = {
                ...generalSetting,
                trafficMonitoring: {
                    daytimeStart: data.daytimeStart,
                    nighttimeStart: data.nighttimeStart,
                    cautionColor: data.cautionColor,
                    warningColor: data.warningColor,
                }
            };

            await uploadData({
                key: 'setting/general_setting.json',
                data: JSON.stringify(newData),
                options: {
                    level: "public",
                    contentType: "text/plain",
                }
            }).result;
            setGeneralSetting(newData);
            success(DIALOG_MESSAGE_UPDATED_GENERAL_SETTING_SUCCESSFULLY);
        } catch (error) {
            errorDialog(TEXT_SAVE_FAILURE);
            console.error("Failed to apply traffic monitoring settings:", error);
        } finally {
            setIsLoading(false);
        }
    });

    const onRemoveCameraSetting = async (routeId, directionIndex, cameraIndex) => {
        try {
            warningConfirm(
                MSG_CONFIRM_DELETE,
                async () => { await removeCameraSetting(routeId, directionIndex, cameraIndex); },
                { confirmText: 'OK', cancelText: 'いいえ' }
            );
        } catch (error) {
            errorDialog(MSG_ERROR);
            console.error("Failed to remove camera setting:", error);
        }
    }

    const removeCameraSetting = async (routeId, directionIndex, cameraIndex) => {
        try {
            const routeSetting = trafficMonitoringSettings.find(route => route.routeId === routeId);
            if (!routeSetting) return;
            setIsLoading(true);
            const cloneRouteSetting = JSON.parse(JSON.stringify(routeSetting));
            if (cloneRouteSetting.trafficDirections.length === 1 && cloneRouteSetting.trafficDirections[0].cameraSettings.length === 1) {
                // if only one direction and one camera, delete the whole route
                const res = await apiDeleteTrafficMonitoring(routeId);
                if (res.success) {
                    success(MSG_DELETE_SUCCESS);
                    setTrafficMonitoringSettings(prev => prev.filter(route => route.routeId !== routeId));
                }
            } else if (cloneRouteSetting.trafficDirections[directionIndex].cameraSettings.length === 1) {
                // If only one camera in the direction, delete the whole direction
                cloneRouteSetting.trafficDirections.splice(directionIndex, 1);
                const res = await apiUpdateTrafficMonitoring(routeId, {
                    trafficDirections: JSON.stringify(cloneRouteSetting.trafficDirections)
                });
                if (res.success) {
                    success(MSG_DELETE_SUCCESS);
                    setTrafficMonitoringSettings(prev => prev.map(route => route.routeId === routeId ? { ...route, trafficDirections: cloneRouteSetting.trafficDirections } : route));
                };
            } else {
                // Otherwise, just delete the camera setting
                cloneRouteSetting.trafficDirections[directionIndex] = {
                    ...cloneRouteSetting.trafficDirections[directionIndex],
                    cameraSettings: cloneRouteSetting.trafficDirections[directionIndex].cameraSettings.filter((_, indx) => indx !== cameraIndex)
                };
                const res = await apiUpdateTrafficMonitoring(routeId, {
                    trafficDirections: JSON.stringify(cloneRouteSetting.trafficDirections)
                });
                if (res.success) {
                    success(MSG_DELETE_SUCCESS);
                    setTrafficMonitoringSettings(prev => prev.map(route => route.routeId === routeId ? { ...route, trafficDirections: cloneRouteSetting.trafficDirections } : route));
                };
            }
        } catch (error) {
            errorDialog(MSG_ERROR);
            console.error("Failed to remove camera setting:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const onCreate = (e) => {
        e.preventDefault();
        navigate(`/traffic-monitoring/new`, {
            state: { routeData: null }
        });
    };

    const onGoToDetail = (e, route) => {
        e.preventDefault();
        navigate(`/traffic-monitoring/${route.routeId}`, {
            state: { routeData: route }
        });
    };

    const onChangePage = (page) => {
        setPagination(prev => ({
            ...prev,
            curPage: page
        }));
    }

    const filteredTrafficMonitoringSettings = useMemo(() => {
        if (!keyword) {
            return trafficMonitoringSettings;
        }

        // Split keyword into multiple keywords by spaces and standardize them
        const keywords = keyword.trim().split(/\s+/).map(k => standardizeString(k)).filter(k => k !== '');

        return trafficMonitoringSettings.filter((route) => {
            // Check if any camera in any direction matches all keywords
            return route.trafficDirections.some(direction => {
                return direction.cameraSettings.some(camera => {
                    const cameraName = SENSOR_MAPPING[camera.cameraId] || '';
                    const cameraInfo = standardizeString(cameraName);

                    // Check if all keywords are present in the camera name
                    return keywords.every(kw => cameraInfo.includes(kw));
                });
            });
        });
    }, [trafficMonitoringSettings, keyword, SENSOR_MAPPING]);

    return (
        <div id='traffic-monitoring'>
            {isLoading ? <FanLoadingIcon size={80} position='fixed' zIndex={9999} /> : null}
            <div className='tm_page-name-contiainer tm_page-name'>
                <LocationListHeader
                    keyword={keyword}
                    onSearch={onSearch}
                    selected={TRAFFIC_MONITORING_LIST}
                />
            </div>
            <div className='tm_container'>
                <div className='tm__top-bar'>
                    <div className='tm__top-bar_right'>
                        <Pagination pagination={pagination} total={filteredTrafficMonitoringSettings.length} onChangePage={onChangePage} />
                        {isAdmin && (
                            <button className='tm__create-button button_cancel' onClick={onCreate}>
                                {TXT_CREATE_NEW}
                            </button>
                        )}
                    </div>
                </div>
                <section className='tm__header'>
                    <div className='tm__field'>
                        <p className='tm__label'>{TXT_START_DAYTIME}</p>
                        <div className='tm__timepicker'>
                            <Controller
                                name="daytimeStart"
                                control={control}
                                render={({ field }) => (
                                    <TimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={!isAdmin}
                                        format="HH:mm"
                                        showMeridian={false}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className='tm__field'>
                        <p className='tm__label'>{TXT_START_NIGHTTIME}</p>
                        <div className='tm__timepicker'>
                            <Controller
                                name="nighttimeStart"
                                control={control}
                                render={({ field }) => (
                                    <TimePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={!isAdmin}
                                        format="HH:mm"
                                        showMeridian={false}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className='tm__field'>
                        <p className='tm__label'>{TXT_CAUTION_COLOR}</p>
                        <input
                            type="color"
                            disabled={!isAdmin}
                            {...register(`cautionColor`)}
                        />
                    </div>
                    <div className='tm__field'>
                        <p className='tm__label'>{TXT_WARNING_COLOR}</p>
                        <input
                            type="color"
                            disabled={!isAdmin}
                            {...register(`warningColor`)}
                        />
                    </div>
                    {isAdmin && (
                        <button
                            onClick={onApplySettings}
                            className='button_active'>
                            {TXT_SAVE}
                        </button>
                    )}

                </section>
                <section style={{ maxHeight: 'calc(100% - 24px)' }}>
                    <div className="tm__table-wrapper">
                        <div className="tm__table-container">
                            <table className="tm__table">
                                <colgroup>
                                    <col style={{ width: "7%" }} />
                                    <col style={{ width: "7%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "2%" }} />
                                    <col style={{ width: "7%" }} />
                                    <col style={{ width: "7%" }} />
                                    <col style={{ width: "5%" }} />
                                    <col style={{ width: "5%" }} />
                                    <col style={{ width: "5%" }} />
                                    <col style={{ width: "5%" }} />
                                    <col style={{ width: "1%" }} />
                                </colgroup>
                                <thead className="tm__table-head">
                                    <tr className="tm__table-row">
                                        <th className="tm__table-header tm__table-text" rowSpan="2">{COL_NAME_MANAGEMENT_ID}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="2">{COL_NAME_ROUTE_NAME}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="2">{COL_NAME_ROUTE_DATA}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="2">{COL_NAME_TRAFFIC_DIRECTION}</th>
                                        <th className="tm__table-header tm__table-text tm__table-header--merged" colSpan="8" rowSpan="1">
                                            {COL_NAME_TRAFFIC_LINKED_SENSORS}
                                            <hr className="tm__separator" />
                                        </th>
                                    </tr>
                                    <tr className="tm__table-row">
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_MANAGEMENT_ID}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_CAMERA}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_SCENARIO_NO}</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_DAYTIME}({COL_NAME_CAUTION})</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_DAYTIME}({COL_NAME_WARNING})</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_NIGHTTIME}({COL_NAME_CAUTION})</th>
                                        <th className="tm__table-header tm__table-text" rowSpan="1">{COL_NAME_NIGHTTIME}({COL_NAME_WARNING})</th>
                                    </tr>
                                </thead>
                                <tbody className="tm__table-body">
                                    <tr className="tm__table-row tm__table-row--separator">
                                        <td className="tm__table-cell" colSpan="12"><hr className="tm__separator" /></td>
                                    </tr>
                                    {filteredTrafficMonitoringSettings.slice((pagination.curPage - 1) * pagination.perPage, pagination.curPage * pagination.perPage).map((route, routeIndex) => {
                                        // Calculate total rows for entire route (all directions and cameras)
                                        const totalRouteRows = route.trafficDirections.reduce((sum, direction) => {
                                            return sum + direction.cameraSettings.length + (direction.cameraSettings.length - 1);
                                        }, 0) + (route.trafficDirections.length - 1);

                                        let isFirstRow = true;

                                        return (
                                            <React.Fragment key={routeIndex}>
                                                {route.trafficDirections.map((direction, directionIndex) => {
                                                    // Calculate total rows for this direction
                                                    const totalDirectionRows = direction.cameraSettings.length + (direction.cameraSettings.length - 1);
                                                    let isFirstDirectionRow = true;

                                                    return (
                                                        <React.Fragment key={`${routeIndex}-${directionIndex}`}>
                                                            {direction.cameraSettings.map((camera, cameraIndex) => (
                                                                <React.Fragment key={`${routeIndex}-${directionIndex}-${cameraIndex}`}>
                                                                    <tr className="tm__table-row">
                                                                        {isFirstRow && (
                                                                            <>
                                                                                <td className="tm__table-cell tm__table-text" rowSpan={totalRouteRows}>{route.routeId}</td>
                                                                                <td className="tm__table-cell tm__table-text" rowSpan={totalRouteRows}>
                                                                                    {isAdmin ? (
                                                                                        <a className="tm__link"
                                                                                            onClick={(e) => onGoToDetail(e, route)}
                                                                                            style={{ cursor: 'pointer' }}
                                                                                        >
                                                                                            {route.routeName}
                                                                                        </a>
                                                                                    ) : (
                                                                                        <p> {route.routeName} </p>
                                                                                    )}

                                                                                </td>
                                                                                <td className="tm__table-cell tm__table-text" rowSpan={totalRouteRows}>{route.routeDataKey}</td>
                                                                            </>
                                                                        )}
                                                                        {isFirstDirectionRow && (
                                                                            <td className="tm__table-cell tm__table-text" rowSpan={totalDirectionRows}>{direction.directionName}</td>
                                                                        )}
                                                                        <td className="tm__table-cell tm__table-text">{cameraIndex + 1}</td>
                                                                        <td className="tm__table-cell tm__table-text">{SENSOR_MAPPING[camera.cameraId]}</td>
                                                                        <td className="tm__table-cell tm__table-text">{camera.scenarioLabel}</td>
                                                                        <td className="tm__table-cell tm__table-text">{camera.daytimeCaution}</td>
                                                                        <td className="tm__table-cell tm__table-text">{camera.daytimeWarning}</td>
                                                                        <td className="tm__table-cell tm__table-text">{camera.nighttimeCaution}</td>
                                                                        <td className="tm__table-cell tm__table-text">{camera.nighttimeWarning}</td>
                                                                        <td className="tm__table-cell tm__table-text">
                                                                            {isAdmin && (
                                                                                <button
                                                                                    className="tm__delete-button"
                                                                                    title="Delete Camera"
                                                                                    onClick={() => onRemoveCameraSetting(route.routeId, directionIndex, cameraIndex)}
                                                                                >
                                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                                </button>
                                                                            )}

                                                                        </td>
                                                                    </tr>
                                                                    {(() => {
                                                                        isFirstRow = false;
                                                                        isFirstDirectionRow = false;
                                                                        return null;
                                                                    })()}
                                                                    {cameraIndex < direction.cameraSettings.length - 1 && (
                                                                        <tr className="tm__table-row tm__table-row--separator">
                                                                            <td className="tm__table-cell" colSpan="8"><hr className="tm__separator" /></td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                            {directionIndex < route.trafficDirections.length - 1 && (
                                                                <tr className="tm__table-row tm__table-row--separator">
                                                                    <td className="tm__table-cell" colSpan="9"><hr className="tm__separator" /></td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                <tr className="tm__table-row tm__table-row--separator">
                                                    <td className="tm__table-cell" colSpan="12"><hr className="tm__separator" /></td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default TrafficMonitoring;